import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Github, Linkedin, Twitter } from "lucide-react";
import { TAGLINE } from "../content/site";
import api from "../lib/api";

const Footer = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const socials = [
    { key: "linkedin", url: settings?.linkedin, Icon: Linkedin, label: "LinkedIn" },
    { key: "twitter", url: settings?.twitter, Icon: Twitter, label: "Twitter / X" },
    { key: "github", url: settings?.github, Icon: Github, label: "GitHub" },
  ].filter((s) => s.url);

  return (
    <footer className="border-t border-tark-line bg-tark-surface/40" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <img src="/tark-logo.png" alt="TARK" className="h-10 w-auto" />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-tark-mut">
              Tark Innovation Private Limited — a global technology engineering company
              building cloud, data &amp; AI and software platforms for premium businesses
              across industries and international markets.
            </p>
            {settings?.contact_email && (
              <a href={`mailto:${settings.contact_email}`} data-testid="footer-contact-email"
                 className="mt-5 inline-block font-mono text-sm text-tark-cyan hover:underline">
                {settings.contact_email}
              </a>
            )}
            {socials.length > 0 && (
              <div className="mt-5 flex gap-3">
                {socials.map(({ key, url, Icon, label }) => (
                  <a key={key} href={url} target="_blank" rel="noreferrer" aria-label={label}
                     data-testid={`footer-social-${key}`}
                     className="rounded-lg border border-tark-line p-2.5 text-tark-mut transition-colors hover:border-tark-cyan/50 hover:text-tark-cyan">
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-tark-ink">Services</h4>
            <ul className="mt-5 space-y-2.5 text-sm text-tark-mut">
              <li><Link to="/services/cloud-engineering" className="hover:text-tark-cyan">Cloud Engineering</Link></li>
              <li><Link to="/services/devops-platform-engineering" className="hover:text-tark-cyan">DevOps &amp; Platform Engineering</Link></li>
              <li><Link to="/data-ai" className="hover:text-tark-cyan">Data &amp; AI</Link></li>
              <li><Link to="/services/custom-software-development" className="hover:text-tark-cyan">Software Engineering</Link></li>
              <li><Link to="/services/enterprise-application-development" className="hover:text-tark-cyan">Enterprise Applications</Link></li>
              <li><Link to="/services/quality-engineering-testing" className="hover:text-tark-cyan">Quality Engineering</Link></li>
              <li><Link to="/services/managed-technology-services" className="hover:text-tark-cyan">Managed Services</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-tark-ink">Company</h4>
            <ul className="mt-5 space-y-2.5 text-sm text-tark-mut">
              <li><Link to="/company" className="hover:text-tark-cyan">About TARK</Link></li>
              <li><Link to="/solutions" className="hover:text-tark-cyan">Solutions</Link></li>
              <li><Link to="/industries" className="hover:text-tark-cyan">Industries</Link></li>
              <li><Link to="/insights" className="hover:text-tark-cyan">Insights</Link></li>
              <li><Link to="/contact" data-testid="footer-contact-link" className="hover:text-tark-cyan">Contact</Link></li>
              <li><Link to="/start-a-project" className="hover:text-tark-cyan">Start a Project</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-tark-cyan">Careers</h4>
            <ul className="mt-5 space-y-2.5 text-sm text-tark-mut">
              <li><Link to="/careers" data-testid="footer-careers-link" className="hover:text-tark-cyan">Current Openings</Link></li>
              <li><Link to="/careers#why-tark" className="hover:text-tark-cyan">Why TARK</Link></li>
              <li><Link to="/careers#hiring-process" className="hover:text-tark-cyan">Hiring Process</Link></li>
              <li><Link to="/careers#life-at-tark" className="hover:text-tark-cyan">Life at TARK</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-tark-line pt-8 sm:flex-row sm:items-center">
          <p className="text-xs text-tark-dim">© 2023 TARK Innovation. All rights reserved.</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-tark-dim">{TAGLINE}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
